import React, { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import {
  API_GOOGLE_LOCATIONS,
  API_RESOLVE_FAILING_SN
} from '../utils/constants';
import { isGoogleLocationConnectionTimeout } from '../pages/Onboarding/googleLocation';
import { useSelector } from 'react-redux';
import { authSelector } from '../store/slices/auth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const axios = require('axios');

const NewGoogleConnection = ({
  open,
  onOpenChange,
  currentClientId,
  currentClient,
  connectSnCallback,
  skipLabel = 'Cancel'
}) => {
  const { isAuthenticated, userInfo } = useSelector(authSelector);
  const [state, setState] = useState({
    optionsData: null,
    loaded: false,
    googleLocation: null,
    clientId: null,
    savingClient: false
  });
  const [isFailing, setIsFailing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationsError, setLocationsError] = useState('');

  const handleResolved = async (snName) => {
    let config = {
      method: 'post',
      url: API_RESOLVE_FAILING_SN,
      data: { clientId: currentClientId, snName },
      headers: { 'Content-Type': 'application/json' }
    };

    if (isFailing) {
      setIsLoading(true);
      await axios(config)
        .then(async (response) => {
          if (response.status === 200) {
            setIsFailing(false);
            setIsLoading(false);
            await someFn();
          }
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (currentClient?.clientSocialNetworks?.gmbFailing !== undefined) {
      setIsFailing(currentClient?.clientSocialNetworks?.gmbFailing);
    } else {
      setIsFailing(false);
    }
  }, [currentClient, currentClientId]);

  const getLocations = useCallback(async () => {
    setLoadingLocations(true);
    setLocationsError('');
    try {
      if (!API_GOOGLE_LOCATIONS) {
        throw new Error('Google Business locations are not configured.');
      }
      const response = await axios.get(API_GOOGLE_LOCATIONS, {
        headers: {
          Authorization: `bearer ${userInfo.accessToken}`
        }
      });
      const pagesData = response.data?.pages;
      if (!Array.isArray(pagesData)) {
        throw new Error('Google returned an invalid locations list.');
      }
      setState((prev) => ({
        ...prev,
        optionsData: pagesData,
        loaded: true
      }));
    } catch (error) {
      console.error('Error fetching Google Business locations:', error);
      setLocationsError(
        error.response?.data?.message ||
          error.message ||
          'Could not load Google Business locations.'
      );
    } finally {
      setLoadingLocations(false);
    }
  }, [userInfo?.accessToken]);

  useEffect(() => {
    if (open) getLocations();
  }, [getLocations, open]);

  const handleSelectChanges = (option) => {
    if (option) {
      const options = { id: option.value, locationName: option.label };
      setState((prev) => ({
        ...prev,
        googleLocation: options,
        clientId: currentClientId
      }));
    }
  };

  const googlePageConnectionHandler = async () => {
    setState((prev) => ({ ...prev, savingClient: true }));
    if (state.googleLocation === undefined || state.googleLocation === null) {
      setLocationsError('Please select a Google Business location.');
      setState((prev) => ({ ...prev, savingClient: false }));
      return;
    }

    const bodyPut = {
      clientId: state.clientId,
      googleLocationId: state.googleLocation.id,
      googleLocationName: state.googleLocation.locationName
    };

    const url = API_GOOGLE_LOCATIONS;

    try {
      if (!state.clientId) {
        throw new Error(
          'The suite client must be created before connecting Google.'
        );
      }
      const response = await axios.put(url, bodyPut, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `bearer ${userInfo.accessToken}`
        }
      });
      if (response.status !== 200) {
        throw new Error('Google Business location could not be connected.');
      }
      await someFn(state.googleLocation);
      onOpenChange(false);
    } catch (error) {
      if (isGoogleLocationConnectionTimeout(error)) {
        console.warn(
          'Google Business connection timed out; verifying it from onboarding.',
          error
        );
        try {
          await someFn(state.googleLocation, { pendingVerification: true });
          onOpenChange(false);
          return;
        } catch (callbackError) {
          console.error(
            'Could not continue to onboarding after Google connection timeout:',
            callbackError
          );
          setLocationsError(
            callbackError.message ||
              'Could not continue to onboarding. Please try again.'
          );
          return;
        }
      }

      console.error('Error connecting Google Business location:', error);
      setLocationsError(
        error.response?.data?.message ||
          error.message ||
          'Could not connect this Google Business location.'
      );
    } finally {
      setState((prev) => ({ ...prev, savingClient: false }));
    }
  };

  const someFn = async (location, options) => {
    if (connectSnCallback) await connectSnCallback(location, options);
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      borderRadius: '0.5rem',
      borderColor: state.isFocused
        ? 'hsl(var(--primary))'
        : 'hsl(var(--border))',
      boxShadow: state.isFocused ? '0 0 0 1px hsl(var(--primary))' : 'none',
      '&:hover': {
        borderColor: 'hsl(var(--primary))'
      }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      zIndex: 50
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'hsl(var(--primary))'
        : state.isFocused
        ? 'hsl(var(--accent))'
        : 'transparent',
      cursor: 'pointer'
    })
  };

  const { optionsData } = state;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && state.savingClient) return;
        onOpenChange(nextOpen);
      }}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Connect Google Business Profile
          </DialogTitle>
          <DialogDescription>
            Select the Google Business location for{' '}
            {currentClient?.clientName || 'this client'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Google Location Selector */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">
                Google Business Location
              </Label>
              <span className="text-sm text-muted-foreground">
                {optionsData?.length || 0} locations available
              </span>
            </div>
            <Select
              options={optionsData?.map((option) => ({
                value: option.id,
                label: option.locationName
              }))}
              onChange={handleSelectChanges}
              isSearchable
              isClearable
              placeholder="Search for a business location..."
              isLoading={loadingLocations}
              styles={customSelectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
            />
            <p className="text-xs text-muted-foreground">
              Select the Google My Business location you want to connect
            </p>
          </div>

          {locationsError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{locationsError}</AlertDescription>
            </Alert>
          )}

          {/* Failing Status Alert */}
          {isAuthenticated &&
            userInfo.role === 'superAdmin' &&
            isFailing &&
            currentClient?.clientSocialNetworks?.googleMyBusiness && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium mb-1">
                      This Google connection is failing
                    </p>
                    <p className="text-xs">
                      Currently connected location may have errors
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="resolve-google-checkbox"
                      className="text-xs cursor-pointer">
                      Mark as resolved
                    </label>
                    <input
                      id="resolve-google-checkbox"
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer"
                      onChange={() => handleResolved('googleMyBusiness')}
                      checked={!isFailing}
                      disabled={isLoading}
                    />
                    {isLoading && (
                      <Loader2 className="h-4 w-4 animate-spin ml-1" />
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

          {/* Success indicator when location is selected */}
          {state.googleLocation && (
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>
                Selected: <strong>{state.googleLocation.locationName}</strong>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={state.savingClient}>
            {skipLabel}
          </Button>
          <Button
            onClick={googlePageConnectionHandler}
            disabled={state.savingClient || !state.googleLocation}
            className="min-w-[120px]">
            {state.savingClient ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect Location'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewGoogleConnection;
